/**
 * Datos iniciales: entidades financieras colombianas y árbol de categorías.
 *
 * Se cargan una sola vez, la primera vez que abres la app. Después son tuyos:
 * puedes renombrar, archivar o agregar lo que quieras sin que esto los pise.
 *
 * El árbol de categorías salió de la investigación (ver
 * investigacion/01-finanzas-personales-colombia.md §7), no de la imaginación.
 * Está pensado para Colombia: incluye 4x1000, SOAT, tecnomecánica, mototaxi,
 * diezmo, estampillas de contratos públicos y aportes de contratista.
 */

import { NATURALEZA, crearCategoria, crearEntidad, nuevoId } from './db.js';

const { NECESIDAD: N, DESEO: D, AHORRO_DEUDA: AD } = NATURALEZA;

/**
 * Entidades financieras.
 *
 * `alias` son los textos que aparecen en sus comprobantes. Los usa el lector
 * de fotos para saber de qué banco es el comprobante.
 */
export const ENTIDADES = [
  // ── Billeteras y neobancos ──
  { nombre: 'Nequi',        tipo: 'billetera', color: '#200040', alias: ['nequi', 'bancolombia s.a. nequi'] },
  { nombre: 'Daviplata',    tipo: 'billetera', color: '#ED1C24', alias: ['daviplata', 'davi plata'] },
  { nombre: 'Nu',           tipo: 'neobanco',  color: '#820AD1', alias: ['nu', 'nubank', 'nu colombia'] },
  { nombre: 'Lulo Bank',    tipo: 'neobanco',  color: '#00E6A0', alias: ['lulo', 'lulo bank'] },
  { nombre: 'Movii',        tipo: 'billetera', color: '#FF4E00', alias: ['movii'] },
  { nombre: 'RappiPay',     tipo: 'billetera', color: '#FF441F', alias: ['rappipay', 'rappi pay'] },
  { nombre: 'Ualá',         tipo: 'billetera', color: '#FF4E63', alias: ['uala', 'ualá'] },

  // ── Bancos ──
  { nombre: 'Bancolombia',        tipo: 'banco', color: '#FDDA24', alias: ['bancolombia', 'bancolombia a la mano'] },
  { nombre: 'Banco de Bogotá',    tipo: 'banco', color: '#00489A', alias: ['banco de bogota', 'banco de bogotá', 'bogota'] },
  { nombre: 'Davivienda',         tipo: 'banco', color: '#ED1C24', alias: ['davivienda'] },
  { nombre: 'BBVA',               tipo: 'banco', color: '#004481', alias: ['bbva', 'bbva colombia'] },
  { nombre: 'Banco Popular',      tipo: 'banco', color: '#00833E', alias: ['banco popular', 'popular'] },
  { nombre: 'Banco de Occidente', tipo: 'banco', color: '#00539B', alias: ['occidente', 'banco de occidente'] },
  { nombre: 'Scotiabank Colpatria', tipo: 'banco', color: '#EC111A', alias: ['colpatria', 'scotiabank'] },
  { nombre: 'Itaú',               tipo: 'banco', color: '#EC7000', alias: ['itau', 'itaú'] },
  { nombre: 'Banco Caja Social',  tipo: 'banco', color: '#00539B', alias: ['caja social', 'bcs'] },
  { nombre: 'Banco Agrario',      tipo: 'banco', color: '#046A38', alias: ['agrario', 'banco agrario'] },
  { nombre: 'AV Villas',          tipo: 'banco', color: '#E4002B', alias: ['av villas', 'avvillas'] },
  { nombre: 'Banco Falabella',    tipo: 'banco', color: '#009639', alias: ['falabella'] },
  { nombre: 'Bancoomeva',         tipo: 'cooperativa', color: '#00A0DF', alias: ['bancoomeva', 'coomeva'] },

  // ── Crédito por compra: cada compra es su propio crédito ──
  { nombre: 'Addi',          tipo: 'credito', color: '#1F1F1F', alias: ['addi'], cobra_gmf: false },
  { nombre: 'Sistecrédito',  tipo: 'credito', color: '#E30613', alias: ['sistecredito', 'sistecrédito'], cobra_gmf: false },
];

/**
 * Árbol de categorías de GASTO.
 * Formato: [grupo, [[subcategoría, naturaleza], ...]]
 *
 * La naturaleza (necesidad / deseo / ahorro-deuda) es lo que hace que la
 * regla 50/30/20 salga sola, sin tener que mantener otra lista aparte.
 */
export const CATEGORIAS_GASTO = [
  ['Vivienda', [
    ['Arriendo', N], ['Administración', N], ['Cuota hipoteca', N],
    ['Impuesto predial', N], ['Seguro de hogar', N],
    ['Mantenimiento y reparaciones', N], ['Muebles y electrodomésticos', D],
    ['Decoración', D], ['Aseo del hogar', N],
  ]],
  ['Servicios públicos', [
    ['Energía', N], ['Agua', N], ['Gas', N], ['Aseo', N],
    ['Internet', N], ['Plan de celular', N], ['TV por cable', D],
  ]],
  ['Alimentación', [
    ['Mercado', N], ['Plaza de mercado', N], ['Carnicería', N],
    ['Tienda de barrio', N], ['Comida fuera', D], ['Domicilios', D],
    ['Café y panadería', D], ['Almuerzo del trabajo', N], ['Licor y bebidas', D],
  ]],
  ['Transporte', [
    ['Gasolina', N], ['Peajes', N], ['Parqueadero', N],
    ['Transporte público', N], ['Taxi o aplicación', N], ['Mototaxi', N],
    ['SOAT', N], ['Tecnomecánica', N], ['Impuesto de vehículo', N],
    ['Mantenimiento y repuestos', N], ['Llantas', N], ['Lavado', D],
    ['Seguro todo riesgo', D], ['Multas', N], ['Cuota del vehículo', AD],
  ]],
  ['Salud', [
    ['Aporte a EPS', N], ['Medicina prepagada', D], ['Consultas', N],
    ['Medicamentos', N], ['Copagos', N], ['Odontología', N], ['Óptica', N],
    ['Exámenes y laboratorios', N], ['Terapia o psicología', N],
    ['Gimnasio', D], ['Seguro de vida', N],
  ]],
  ['Educación', [
    ['Matrícula o pensión', N], ['Universidad', N], ['Útiles y uniformes', N],
    ['Cursos y certificaciones', D], ['Libros', D], ['Transporte escolar', N],
  ]],
  ['Personal', [
    ['Ropa y calzado', D], ['Peluquería o barbería', D],
    ['Cuidado personal', N], ['Lavandería', N],
  ]],
  ['Entretenimiento', [
    ['Cine y conciertos', D], ['Salidas y rumba', D], ['Deportes y hobbies', D],
    ['Suscripciones digitales', D], ['Videojuegos', D],
  ]],
  ['Viajes', [
    ['Vuelos', D], ['Hospedaje', D], ['Paseos y excursiones', D],
  ]],
  ['Mascotas', [
    ['Comida de mascota', N], ['Veterinario', N], ['Peluquería canina', D],
    ['Juguetes y accesorios', D],
  ]],
  ['Familia', [
    ['Apoyo a familiares', N], ['Cuota alimentaria', N], ['Hijos', N],
  ]],
  ['Regalos y social', [
    ['Regalos', D], ['Navidad y diciembre', D], ['Día de la madre o padre', D],
    ['Vacas y colectas', D], ['Grados y matrimonios', D],
  ]],
  ['Donaciones', [
    ['Diezmo u ofrenda', D], ['Donaciones', D],
  ]],
  // Este grupo es el que hace visible la plata que se pierde sin darse cuenta.
  ['Financieros', [
    ['4x1000 (GMF)', N], ['Cuota de manejo de tarjeta', N],
    ['Comisiones bancarias', N], ['Cuota de manejo de cuenta', N],
    ['Intereses de tarjeta', AD], ['Intereses de préstamos', AD],
    ['Intereses de mora', AD], ['Comisión por avance', AD],
    ['Seguros de deudores', N], ['Notaría y trámites', N],
  ]],
  ['Impuestos', [
    ['Retención en la fuente', N], ['Impuesto de renta', N],
    ['ICA', N], ['Otros impuestos', N],
  ]],
  // Para el contrato de prestación de servicios con la Asamblea.
  ['Trabajo', [
    ['Aportes a seguridad social', N], ['Herramientas y software', N],
    ['Papelería e insumos', N], ['Transporte de trabajo', N],
    ['Póliza de cumplimiento', N], ['Estampillas y contribuciones', N],
    ['Contador y asesoría', N],
  ]],
  ['Otros', [
    ['Envíos y mensajería', N], ['Recargas de celular', N],
    ['Imprevistos', N], ['Sin categorizar', N],
  ]],
];

export const CATEGORIAS_INGRESO = [
  ['Laboral', [
    ['Salario', N], ['Auxilio de transporte', N], ['Prima de servicios', N],
    ['Cesantías', N], ['Intereses a las cesantías', N], ['Vacaciones', N],
    ['Bonificaciones', N], ['Horas extras', N], ['Liquidación', N],
  ]],
  ['Independiente', [
    ['Honorarios (prestación de servicios)', N], ['Facturación y ventas', N],
    ['Comisiones', N],
  ]],
  ['Negocio', [
    ['Ventas del negocio', N], ['Propinas', N],
  ]],
  ['Rentas', [
    ['Arriendos recibidos', N], ['Alquiler de bienes', N],
  ]],
  ['Financieros', [
    ['Intereses de ahorro', N], ['Rendimientos de inversión', N],
    ['Dividendos', N], ['Venta de activos', N],
  ]],
  ['Otros ingresos', [
    ['Reembolsos', N], ['Regalos recibidos', N], ['Devoluciones', N],
    ['Premios', N], ['Subsidios', N], ['Devolución de la DIAN', N],
  ]],
];

/**
 * Cuentas con las que arranca la app.
 *
 * Solo dos, a propósito. Pedirle a alguien que configure ocho cuentas antes de
 * poder registrar su primer gasto es la forma más rápida de que abandone.
 * Efectivo y Nequi cubren el primer día; lo demás se agrega cuando haga falta.
 */
export const CUENTAS_INICIALES = [
  { nombre: 'Efectivo', tipo: 'efectivo', entidad: null, icono: '💵', orden: 0 },
  { nombre: 'Nequi', tipo: 'billetera', entidad: 'Nequi', icono: '📱', orden: 1 },
];

/** Convierte el árbol en las filas que van a la base de datos. */
export function construirSemilla() {
  const entidades = ENTIDADES.map((e) =>
    crearEntidad({
      nombre: e.nombre,
      tipo: e.tipo,
      alias: e.alias,
      color: e.color,
      cobra_gmf: e.cobra_gmf !== false,
    })
  );

  const categorias = [];
  const agregarArbol = (arbol, tipo) => {
    for (const [grupo, hijos] of arbol) {
      const padre = crearCategoria({
        nombre: grupo,
        tipo,
        naturaleza: hijos[0]?.[1] ?? N,
        orden: categorias.length,
      });
      categorias.push(padre);
      for (const [nombre, naturaleza] of hijos) {
        categorias.push(
          crearCategoria({
            nombre,
            padre_id: padre.id,
            tipo,
            naturaleza,
            orden: categorias.length,
          })
        );
      }
    }
  };

  agregarArbol(CATEGORIAS_GASTO, 'egreso');
  agregarArbol(CATEGORIAS_INGRESO, 'ingreso');

  const porNombre = Object.fromEntries(entidades.map((e) => [e.nombre, e.id]));
  const cuentas = CUENTAS_INICIALES.map((c, i) => ({
    id: nuevoId(),
    nombre: c.nombre,
    tipo: c.tipo,
    entidad_id: c.entidad ? porNombre[c.entidad] : null,
    icono: c.icono,
    orden: i,
    moneda: 'COP',
    saldo_inicial: 0,
    cupo_total: 0,
    alerta_cupo_pct: 70,
    exenta_gmf: false,
    incluir_en_patrimonio: true,
    archivada: false,
    actualizado_en: Date.now(),
    borrado_en: null,
  }));

  return { entidades, categorias, cuentas };
}
